'use client';

import { Product } from "@/lib/TicketApi";
import { useLanguage } from '@/providers/LanguageProvider';
import { useCart } from "@/providers/CartProvider";
import QuantitySelectButton from "@/components/QuantitySelectButton";
import TimeSlotList from "../TimeSlotList";
import { useQueueContext } from '@/providers/QueueContext';
import * as m from "@/paraglide/messages.js"

interface ProductCartButtonProps {
    product: Product;
    // Products whose price comes from a chosen amount (cashcard, cashless wallet) can only be
    // added once that amount is known - the stepper alone can't produce valid product settings.
    requiresAmountSelection?: boolean;
}

export default function ProductAvailable({product, requiresAmountSelection = false}: ProductCartButtonProps) {
    const { isQueueActive } = useQueueContext();
    const {setProductQuantity, selectedQuantity, productLines, loading, totalForProduct, productError, cart, isProductQuantityIncreaseDisabled, isProductQuantityIncreaseDisabledByShopUnlock, getShopUnlockProductLimit} = useCart();
    const { currentLanguage: lang } = useLanguage();

    // A product sold per time slot is bought row by row instead of with one stepper (FAS-1053).
    const hasTimeSlots = (product.timeSlots?.length ?? 0) > 0;

    // The stepper drives the product's most recent line — for an ordinary product that is its only
    // one; for an amount-driven product it is the amount currently selected in the picker above.
    const lines = productLines(product.productId);
    const activeLine = lines.length > 0 ? lines[lines.length - 1] : undefined;
    const activeQuantity = activeLine?.quantity ?? 0;

    let limit = product.maxQuantity;
    if(product.isHidden){
        if(cart === null || cart.appliedCouponCode === null || cart.appliedCouponCode.unlockProducts === null){
            limit = 0;
        } else {
            limit = cart.appliedCouponCode.unlockProducts.products.find(p => p.productId === product.productId)?.quantity ?? 0;
        }
    }

    // For amount-driven products the cart line carries {amount, currency} settings. The stepper
    // never passes settings, so increasing from an empty cart line would reserve without an
    // amount and the API rejects it with "The product settings are invalid: amount is required".
    const amountNotSelected = requiresAmountSelection
        && !(typeof activeLine?.settings?.amount === 'number' && !!activeLine?.settings?.currency);

    // Apply per-product limit from shop unlock coupon (use min of product limit and coupon limit)
    const shopUnlockProductLimit = getShopUnlockProductLimit(product.productId);
    if (shopUnlockProductLimit !== null) {
        limit = Math.min(limit, shopUnlockProductLimit);
    }
    // console.log(product.);

    const handleDecrease = () => {
        const currentQuantity = activeQuantity;
        if (currentQuantity > 0) {
            const newQuantity = currentQuantity - product.stepQuantity;
            // If the new quantity would be less than minQuantity, we need to decide:
            // - If current quantity equals minQuantity, allow going to 0 (remove from cart)
            // - Otherwise, stop at minQuantity
            if (newQuantity < product.minQuantity) {
                if (currentQuantity === product.minQuantity) {
                    // Allow removing the item from cart
                    setProductQuantity(product.productId, 0);
                } else {
                    // Stop at minQuantity
                    setProductQuantity(product.productId, product.minQuantity);
                }
            } else {
                setProductQuantity(product.productId, newQuantity);
            }
        }
    };

    const handleIncrease = () => {
        if (amountNotSelected) {
            return;
        }

        const currentQuantity = activeQuantity;
        
        // If current quantity is 0 and product has minQuantity > 0, set to minQuantity
        if (currentQuantity === 0 && product.minQuantity > 0) {
            setProductQuantity(product.productId, product.minQuantity);
            return;
        }
        
        // Regular increment logic — the limit applies to the product across all of its lines
        const newQuantity = currentQuantity + product.stepQuantity;
        if(selectedQuantity(product.productId) - currentQuantity + newQuantity > limit){
            return;
        }
        
        // Check if this increase would exceed the maxProducts constraint
        if (isProductQuantityIncreaseDisabled(product.productId)) {
            return;
        }
        
        // Check if this increase would exceed the shop unlock maxProducts constraint
        if (isProductQuantityIncreaseDisabledByShopUnlock(product.productId)) {
            return;
        }
        
        setProductQuantity(product.productId, newQuantity);
    };

    const forProduct = totalForProduct(product.productId);

    if(product.inventoryStatus === 'sold_out' && selectedQuantity(product.productId) === 0){
        return <div className="flex flex-col justify-start opacity-60">
            <div className="text-xl font-light py-1">{m.product_sold_out()}</div>
        </div>;
    }

    if(product.inventoryStatus === 'all_reserved' && selectedQuantity(product.productId) === 0){
        return <div className="flex flex-col justify-start opacity-60">
            <div className="text-xl font-light py-1">{m.product_all_reserved()}</div>
            <div>{m.product_all_reserved_description()}</div>
        </div>;
    }
    
    return (
        <>
        {hasTimeSlots && !isQueueActive && (
            <TimeSlotList product={product} limit={limit} />
        )}
        <div className="flex justify-between py-2">
            <div className="text-base font-bold">
                {forProduct !== null ? forProduct.totalInclFee.display(lang) : ''}
            </div>
            {!isQueueActive && !hasTimeSlots && (
                <div className="flex flex-col items-end gap-1">
                    <QuantitySelectButton
                        quantity={activeQuantity}
                        limit={limit}
                        loading={loading}
                        onIncrease={handleIncrease}
                        onDecrease={handleDecrease}
                        increaseDisabled={amountNotSelected || isProductQuantityIncreaseDisabled(product.productId) || isProductQuantityIncreaseDisabledByShopUnlock(product.productId)}
                    />
                    {shopUnlockProductLimit !== null && (
                        <span className="text-xs opacity-70">
                            {m.access_code_product_limit({ limit: shopUnlockProductLimit })}
                        </span>
                    )}
                </div>
            )}
        </div>
            
            {productError(product.productId) && 
            <div className="font-bold text-red-500 mt-2 -ml-2 p-2 bg-red-100 rounded-md">
                {productError(product.productId)}
            </div>}
        </>
    )
}